/** Difficulty levels for Activity points. Stored as integers; the DB has no constraint — validate against this list. */
export const ACTIVITY_LEVELS = [
  { level: 1, key: 'easier', label: 'Easier' },
  { level: 2, key: 'default', label: 'Default' },
  { level: 3, key: 'harder', label: 'Harder' },
] as const;

/** Stable API key for a level (response property names). Unlike the label, it never changes. */
export type ActivityLevelKey = (typeof ACTIVITY_LEVELS)[number]['key'];

/** The level a details screen opens on when nothing is saved. */
export const DEFAULT_ACTIVITY_LEVEL_KEY: ActivityLevelKey = 'default';

export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number]['level'];

/** Default level as stored (ACTIVITY_LEVELS value); what a child sees until a level is saved. */
export const DEFAULT_ACTIVITY_LEVEL: ActivityLevel = 2;

export function activityLevelKey(level: ActivityLevel): ActivityLevelKey {
  return ACTIVITY_LEVELS.find((l) => l.level === level)!.key;
}

export function activityLevelFromKey(key: ActivityLevelKey): ActivityLevel {
  return ACTIVITY_LEVELS.find((l) => l.key === key)!.level;
}

export const ACTIVITY_LEVEL_VALUES: readonly number[] = ACTIVITY_LEVELS.map((l) => l.level);

export function isActivityLevel(value: number): value is ActivityLevel {
  return ACTIVITY_LEVEL_VALUES.includes(value);
}

export function activityLevelLabel(level: number): string {
  return ACTIVITY_LEVELS.find((l) => l.level === level)?.label ?? `Level ${level}`;
}

export const ACTIVITY_DESCRIPTION_MAX_LENGTH = 120;
/** Max length for whyItWorks, materialsNeeded and each point's text */
export const ACTIVITY_TEXT_MAX_LENGTH = 250;
/** Max points per difficulty level */
export const ACTIVITY_MAX_POINTS_PER_LEVEL = 20;

export const ACTIVITY_STATUSES = ['draft', 'published', 'unpublished', 'archived'] as const;
export type ActivityStatusValue = (typeof ACTIVITY_STATUSES)[number];

export const ACTIVITY_STATUS_TRANSITIONS: Record<ActivityStatusValue, readonly ActivityStatusValue[]> = {
  draft: ['published', 'archived'],
  published: ['unpublished', 'archived'],
  unpublished: ['published', 'archived'],
  archived: [],
};

export interface ActivityRuleError {
  /** Same path the backend uses: 'title' | 'description' | 'whyItWorks' | 'domainId' | 'skillIds' | 'points' | 'points.level{n}' */
  path: string;
  message: string;
}

export interface ActivityRuleInput {
  title?: string | null;
  description?: string | null;
  whyItWorks?: string | null;
  domainId?: number | null;
  skillIds: readonly number[];
  points: ReadonlyArray<{ level: number; text: string }>;
}

export const activityPointsPath = (level: number) => `points.level${level}`;

const isBlank = (value: string | null | undefined) => !value || !value.trim();

/** The single rule set, applied on every save (create and edit, any status) and before publishing. */
export function getActivityRuleErrors(input: ActivityRuleInput): ActivityRuleError[] {
  const errors: ActivityRuleError[] = [];

  if (isBlank(input.title)) errors.push({ path: 'title', message: 'Title is required' });

  if (isBlank(input.description)) {
    errors.push({ path: 'description', message: 'Description is required' });
  } else if (input.description!.trim().length > ACTIVITY_DESCRIPTION_MAX_LENGTH) {
    errors.push({
      path: 'description',
      message: `Description must be ${ACTIVITY_DESCRIPTION_MAX_LENGTH} characters or less`,
    });
  }

  if (isBlank(input.whyItWorks)) errors.push({ path: 'whyItWorks', message: 'Why it works is required' });
  if (!input.domainId) errors.push({ path: 'domainId', message: 'Primary domain is required' });
  if (input.skillIds.length === 0) errors.push({ path: 'skillIds', message: 'At least one skill is required' });

  if (input.points.some((p) => !isActivityLevel(p.level))) {
    errors.push({
      path: 'points',
      message: `Level must be one of ${ACTIVITY_LEVEL_VALUES.join(', ')}`,
    });
  }

  for (const { level, label } of ACTIVITY_LEVELS) {
    const levelPoints = input.points.filter((p) => p.level === level);
    if (levelPoints.length === 0) {
      errors.push({ path: activityPointsPath(level), message: `${label} (level ${level}) needs at least one point` });
    } else if (levelPoints.length > ACTIVITY_MAX_POINTS_PER_LEVEL) {
      errors.push({
        path: activityPointsPath(level),
        message: `${label} (level ${level}) allows at most ${ACTIVITY_MAX_POINTS_PER_LEVEL} points`,
      });
    } else if (levelPoints.some((p) => isBlank(p.text))) {
      errors.push({ path: activityPointsPath(level), message: `${label} (level ${level}) has an empty point` });
    }
  }

  return errors;
}
